<template>
  <!-- Breadcrumbs -->
  <BaseContainer>
    <div class="flex gap-x-3 py-4">
      <NuxtLinkLocale to="/">{{ $t('products.home') }}</NuxtLinkLocale>
      <div>/</div>
      <NuxtLinkLocale to="/styles">
        {{ $t('products.allStyle') }}
      </NuxtLinkLocale>
      <template v-if="!!search">
        <div>/</div>
        <div>{{ $t('products.search') }}: {{ search }}</div>
      </template>
    </div>
  </BaseContainer>

  <!-- Banner -->
  <section v-if="!!widgetData?.mobileImageUrl || !!widgetData?.imageUrl">
    <div
      v-if="!!widgetData?.mobileImageUrl || !!widgetData?.imageUrl"
      :key="widgetData?.mobileImageUrl"
      class="bg-primary-tangerine relative lg:hidden block"
    >
      <img
        :src="widgetData?.mobileImageUrl || widgetData?.imageUrl"
        alt="cover image"
        class="w-full h-full left-0 top-0 object-cover object-center"
      />
    </div>
    <div
      v-if="!!widgetData?.imageUrl"
      :key="widgetData?.imageUrl"
      class="bg-primary-tangerine relative hidden lg:block"
    >
      <img
        :src="widgetData?.imageUrl"
        alt="cover image"
        class="w-full h-full left-0 top-0 object-cover object-center"
      />
    </div>
  </section>
  <section v-else :class="'bg-primary-tangerine relative'">
    <img
      alt="all styles"
      src="/images/shop-all-styles.jpg"
      class="w-full h-full left-0 top-0 object-cover object-center"
    />
  </section>

  <!-- Stickers -->
  <section class="space-y-16 py-16">
    <StickerListSwiper
      v-for="(collection, index) in collections"
      :key="index"
      :index="index"
      :slide-key="`collection-style-` + index"
      :title="
        locale === 'en'
          ? collection[0].headerEn || ''
          : collection[0].headerTh || collection[0].headerEn || ''
      "
      :data="collection"
      recommendation
      style
    />
  </section>
</template>

<script lang="ts" setup>
import { find } from 'lodash-es'
import { cmsRepository } from '~/repository/cmsRepository'
import { seoRepository } from '~/repository/seoRepository'

const route = useRoute()
const search = computed(() => (route.query.search as string) || '')

const { locale } = useI18n()
const { $serverApi } = useNuxtApp()
const seoRepo = seoRepository($serverApi)

const { data: pageDataAllStatic } = await useAsyncData(
  'getSeoProductByAllStylePage',
  async () => {
    const data = await seoRepo.getSeo('product-by-style-all')
    return data
  }
)

const seotTitleAll =
  locale.value === 'en'
    ? pageDataAllStatic.value?.meta_title?.en
    : pageDataAllStatic.value?.meta_title?.th ||
      pageDataAllStatic.value?.meta_title?.en

const seoDescriptionAll =
  locale.value === 'en'
    ? pageDataAllStatic.value?.meta_description?.en
    : pageDataAllStatic.value?.meta_description?.th ||
      pageDataAllStatic.value?.meta_description?.en

useSeoMeta({
  title: seotTitleAll,
  description: seoDescriptionAll,
  ogImage: pageDataAllStatic.value?.og_image,
  keywords: pageDataAllStatic.value?.meta_keyword
})

const cmsRepo = cmsRepository($serverApi)

const { data: collections } = await useAsyncData<
  {
    headerEn?: string
    headerTh?: string
    imageUrl?: string
    mobileImageUrl?: string
    titleEn: string
    titleTh: string
    descriptionEn: string
    descriptionTh?: string
    href?: string
  }[][]
>('getStylePageCollection', async () => {
  const response = await cmsRepo.getWidget('style-page-stickers')
  if (response.data.data.items) return response.data.data.items
  return null
})

const { data: bannerData } = await useAsyncData<
  {
    imageUrl: string
    mobileImageUrl?: string
    pathname: string
  }[]
>('getBanners/', async () => {
  const response = await cmsRepo.getWidget('banners')
  return response.data.data.items
})

const widgetData = computed(() => {
  const banner = find(bannerData.value, (item) =>
    item.pathname
      ?.split(',')
      .map((item) => item.trim())
      .includes(route.fullPath.replace('/en', '').replace('/th', ''))
  )

  return banner
})
</script>
